import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { apiListGiving, apiDeleteGiving } from "./api";
import { CustomIcon } from "../shared/CustomIcon";
import GivingFormModal from "./GivingFormModal";
import { apiListGiving as apiListFinanceGiving, apiListReceipts } from "../finance/api";
import { generateReceiptPdf } from "../finance/TaxReceiptPdf";
import { useAppSettings, resolveLogo } from "../../lib/AppSettingsContext";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

// =============================================================
// MY GIVING — private personal ledger + read-only mirror of
// Finance-recorded official gifts for the same member.
//
// Layout:
//   1. Combined Summary Card (Cinzel gold) — year selector +
//      Official / Personal / Combined totals + Download PDF.
//   2. Official Giving Records section — filter controls (year,
//      date range, categories) + record list.
//   3. Personal ledger — grouped by year, expand/collapse.
//
// SA / LA can pass ?userId= in the URL to view another user's
// ledger in read-only mode for oversight.
// =============================================================

function fmtCurrency(n, symbol = "$") {
  if (!Number.isFinite(Number(n))) return `${symbol}0.00`;
  return `${symbol}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCents(cents, symbol = "$") {
  return fmtCurrency((Number(cents) || 0) / 100, symbol);
}

function startOfWeek(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  return x;
}

const CATEGORY_ICONS = {
  Tithe: "dove",
  Offering: "praying-hands",
  "Special Donation": "sparkle",
  "Building Fund": "church",
  Other: "gift",
};

function CatIcon({ category, size = 14 }) {
  const name = CATEGORY_ICONS[category] || "gift";
  return <CustomIcon name={name} size={size} />;
}

// The exact vocab that Finance uses when recording official giving.
const OFFICIAL_CATEGORIES = ["Regular Offering", "Tithe", "Seed", "Alms", "Other"];

export default function GivingPage({ currentUser, setCurrentUser }) {
  const [search] = useSearchParams();
  const targetUserId = search.get("userId") || currentUser?.id;
  const readonly = targetUserId !== currentUser?.id;
  const { settings: appSettings } = useAppSettings();
  const currencySymbol = appSettings?.currencySymbol || "$";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [period, setPeriod] = useState("Year");
  const [expandedYears, setExpandedYears] = useState({});

  // Official giving (from Finance) — lifted up so the summary card + PDF export can share it.
  const [officialRecords, setOfficialRecords] = useState([]);
  const [officialLoading, setOfficialLoading] = useState(true);
  // Tax receipts issued to this member
  const [receipts, setReceipts] = useState([]);

  // Filters + selected view-year (drives summary card, PDF period, and the filter panel).
  const currentYear = new Date().getFullYear();
  const [viewYear, setViewYear] = useState(currentYear);
  const [officialDateFrom, setOfficialDateFrom] = useState("");
  const [officialDateTo, setOfficialDateTo] = useState("");
  const [officialCategories, setOfficialCategories] = useState([]);

  // Iter 84 — MyGiving widget preferences (per-user, server-persisted).
  const mgwPref = (currentUser?.preferences && typeof currentUser.preferences.myGivingWidget === "object")
    ? currentUser.preferences.myGivingWidget
    : {};
  const [mgwPeriod, setMgwPeriod] = useState(["Weekly","Monthly","Yearly"].includes(mgwPref.period) ? mgwPref.period : "Yearly");
  const [mgwShowDiff, setMgwShowDiff] = useState(mgwPref.showDifference === false ? false : true);
  const [mgwSaving, setMgwSaving] = useState(false);
  const [mgwSavedAt, setMgwSavedAt] = useState(0);
  const [mgwOpen, setMgwOpen] = useState(false);
  useEffect(() => {
    const p = currentUser?.preferences?.myGivingWidget || {};
    if (["Weekly","Monthly","Yearly"].includes(p.period)) setMgwPeriod(p.period);
    if (p.showDifference !== undefined) setMgwShowDiff(!!p.showDifference);
  }, [currentUser?.preferences?.myGivingWidget]);
  const saveMgwPrefs = useCallback(async (patch) => {
    setMgwSaving(true);
    try {
      const res = await kcFetch(`${API_BASE}/me/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ myGivingWidget: patch }),
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const json = await res.json();
      // Reflect the update on the local currentUser so the dashboard widget
      // rereads without a page refresh.
      if (typeof setCurrentUser === "function") {
        setCurrentUser(prev => prev ? { ...prev, preferences: json.preferences } : prev);
      }
      setMgwSavedAt(Date.now());
    } catch (e) {
      alert(e.message || "Could not save preferences");
    } finally {
      setMgwSaving(false);
    }
  }, [setCurrentUser]);

  // ---- Load personal ledger ----
  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await apiListGiving(targetUserId);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Could not load giving records");
      setItems([]);
    }
    setLoading(false);
  }, [targetUserId]);
  useEffect(() => { reload(); }, [reload]);

  // ---- Load official Finance records for this member ----
  // Pass memberId explicitly so Finance-privileged users viewing their own
  // /giving page only see their own records (backend already scopes non-Finance
  // callers to their own memberId, so this is a no-op for them).
  useEffect(() => {
    let cancel = false;
    const targetMemberId = currentUser?.memberId;
    setOfficialLoading(true);
    if (!targetMemberId) {
      // User isn't linked to a member profile — nothing to fetch.
      setOfficialRecords([]);
      setOfficialLoading(false);
      return;
    }
    apiListFinanceGiving({ isVoided: "false", memberId: targetMemberId })
      .then(r => { if (!cancel) setOfficialRecords(Array.isArray(r) ? r : []); })
      .catch(() => { if (!cancel) setOfficialRecords([]); })
      .finally(() => { if (!cancel) setOfficialLoading(false); });
    // Receipts (issued to this member) — used by the Receipts section further down.
    apiListReceipts({ memberId: targetMemberId })
      .then(r => { if (!cancel) setReceipts(Array.isArray(r) ? r : []); })
      .catch(() => { if (!cancel) setReceipts([]); });
    return () => { cancel = true; };
  }, [currentUser?.memberId]);

  const remove = async (id) => {
    if (!window.confirm("Delete this giving entry? This cannot be undone.")) return;
    try { await apiDeleteGiving(targetUserId, id); await reload(); }
    catch (e) { alert(e.message || "Could not delete"); }
  };

  // Year-grouped personal ledger (Map → sorted array)
  const yearGroups = useMemo(() => {
    const out = new Map();
    for (const it of items) {
      const y = new Date(it.date).getFullYear() || new Date().getFullYear();
      if (!out.has(y)) out.set(y, { year: y, rows: [], total: 0 });
      const g = out.get(y);
      g.rows.push(it);
      g.total += Number(it.amount) || 0;
    }
    for (const g of out.values()) g.rows.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return Array.from(out.values()).sort((a, b) => b.year - a.year);
  }, [items]);

  // Every year that has EITHER personal or official records — used to
  // populate the year selector.
  const allYears = useMemo(() => {
    const s = new Set([currentYear]);
    yearGroups.forEach(g => s.add(g.year));
    officialRecords.forEach(r => { const y = new Date(r.date).getFullYear(); if (!isNaN(y)) s.add(y); });
    return Array.from(s).sort((a, b) => b - a);
  }, [yearGroups, officialRecords, currentYear]);

  // -------- Filtered official records for the selected view --------
  const officialForYear = useMemo(
    () => officialRecords.filter(r => new Date(r.date).getFullYear() === viewYear),
    [officialRecords, viewYear]
  );

  const filteredOfficial = useMemo(() => {
    return officialForYear.filter(r => {
      if (officialDateFrom && r.date < officialDateFrom) return false;
      if (officialDateTo && r.date > officialDateTo) return false;
      if (officialCategories.length > 0 && !officialCategories.includes(r.category)) return false;
      return true;
    });
  }, [officialForYear, officialDateFrom, officialDateTo, officialCategories]);

  // -------- Summary totals for the selected year --------
  const officialTotalCents = useMemo(
    () => officialForYear.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
    [officialForYear]
  );
  const personalTotal = useMemo(() => {
    return items
      .filter(it => new Date(it.date).getFullYear() === viewYear)
      .reduce((s, it) => s + (Number(it.amount) || 0), 0);
  }, [items, viewYear]);
  const combinedTotal = officialTotalCents / 100 + personalTotal;

  // Filter chips flag (used to enable Clear Filters button)
  const anyOfficialFilterActive = !!officialDateFrom || !!officialDateTo || officialCategories.length > 0;

  const clearOfficialFilters = () => {
    setOfficialDateFrom("");
    setOfficialDateTo("");
    setOfficialCategories([]);
  };

  const toggleOfficialCategory = (c) => {
    setOfficialCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  // -------- PDF Download --------
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await generateGivingPdf({
        appSettings,
        currencySymbol,
        member: currentUser,
        viewYear,
        dateFrom: officialDateFrom,
        dateTo:   officialDateTo,
        records:  filteredOfficial,
      });
    } catch (e) {
      alert(e?.message || "Could not generate PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ---- Personal ledger period subtotal (unchanged behaviour) ----
  const periodSubtotal = useMemo(() => {
    const now = new Date();
    let from = new Date(now);
    if (period === "Day")   { from.setHours(0, 0, 0, 0); }
    if (period === "Week")  { from = startOfWeek(now); }
    if (period === "Month") { from = new Date(now.getFullYear(), now.getMonth(), 1); }
    if (period === "Year")  { from = new Date(now.getFullYear(), 0, 1); }
    return items
      .filter(it => new Date(it.date) >= from && new Date(it.date) <= now)
      .reduce((s, it) => s + (Number(it.amount) || 0), 0);
  }, [items, period]);

  // Initial year-collapse state
  useEffect(() => {
    const init = {};
    yearGroups.forEach(g => { init[g.year] = (g.year === currentYear); });
    setExpandedYears(prev => ({ ...init, ...prev }));
  }, [yearGroups.length]);

  const officialHasAny = officialForYear.length > 0;

  return (
    <div data-testid="giving-page" style={{ background: "var(--kc-surface)", minHeight: "100vh", padding: "24px clamp(16px,4vw,40px)", color: "var(--kc-text)" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div>
            <div data-testid="giving-page-title" style={{ fontFamily: "'Cinzel',serif", fontSize: 24, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase" }}>
              My Giving
            </div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-sub)", marginTop: 4 }}>
              {readonly
                ? `Viewing as Admin — read-only`
                : `Your private personal ledger. Only you can see and edit these entries.`}
            </div>
          </div>
          {!readonly && (
            <button
              className="btn btn-gold"
              data-testid="giving-add-btn"
              onClick={() => { setEditEntry(null); setShowForm(true); }}
              style={{ background: "var(--kc-accent)", color: "#1A1A1A", fontWeight: 700 }}
            >+ New Entry</button>
          )}
        </div>

        {/* Widget Display Settings (Iter 84) — user-configurable per-user preferences
            that control what My Giving widget shows on the Home dashboard. */}
        {!readonly && (
          <div data-testid="mygiving-widget-settings" style={{ marginBottom: 16, background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)", borderRadius: 12, overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setMgwOpen(v => !v)}
              data-testid="mygiving-widget-settings-toggle"
              style={{
                width: "100%", padding: "12px 16px", background: "transparent", border: "none",
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left",
                color: "var(--kc-text)",
              }}
            >
              <CustomIcon name="settings" size={16} />
              <span style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>Widget Display Settings</span>
              <span style={{ flex: 1 }} />
              {mgwSaving && <span style={{ fontSize: 11, color: "var(--kc-sub)" }}>Saving…</span>}
              {!mgwSaving && mgwSavedAt > 0 && (Date.now() - mgwSavedAt) < 4000 && (
                <span style={{ fontSize: 11, color: "var(--kc-green)" }}>Saved ✓</span>
              )}
              <span style={{ transition: "transform 0.15s", transform: mgwOpen ? "rotate(90deg)" : "rotate(0deg)", color: "var(--kc-sub)", fontSize: 12 }}>▶</span>
            </button>
            {mgwOpen && (
              <div style={{ padding: "0 16px 16px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 8 }}>
                    Period on My Giving dashboard widget
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} role="tablist" data-testid="mygiving-widget-period-selector">
                    {["Weekly","Monthly","Yearly"].map(p => (
                      <button
                        key={p}
                        type="button"
                        data-testid={`mygiving-widget-period-${p}`}
                        aria-pressed={mgwPeriod === p}
                        onClick={() => { setMgwPeriod(p); saveMgwPrefs({ period: p, showDifference: mgwShowDiff }); }}
                        style={{
                          padding: "8px 16px",
                          borderRadius: 999,
                          border: `1px solid ${mgwPeriod === p ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 25%, transparent)"}`,
                          background: mgwPeriod === p ? "var(--kc-accent)" : "transparent",
                          color: mgwPeriod === p ? "#111" : "var(--kc-text)",
                          fontWeight: 700, fontSize: 12,
                          letterSpacing: "0.04em", textTransform: "uppercase",
                          cursor: "pointer",
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <label
                  htmlFor="mygiving-widget-showdiff"
                  style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: "var(--kc-text)" }}
                  data-testid="mygiving-widget-showdiff-label"
                >
                  <input
                    id="mygiving-widget-showdiff"
                    type="checkbox"
                    checked={mgwShowDiff}
                    onChange={(e) => { const v = e.target.checked; setMgwShowDiff(v); saveMgwPrefs({ period: mgwPeriod, showDifference: v }); }}
                    data-testid="mygiving-widget-showdiff"
                    style={{ width: 18, height: 18, accentColor: "var(--kc-accent)" }}
                  />
                  <span>Show difference between official and personal records</span>
                </label>
                <div style={{ fontSize: 11, color: "var(--kc-meta)", lineHeight: 1.5 }}>
                  These preferences are stored on your profile and apply to the My Giving widget on your Home dashboard across devices.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ COMBINED SUMMARY CARD ============ */}
        <CombinedSummaryCard
          viewYear={viewYear}
          allYears={allYears}
          onYearChange={setViewYear}
          officialTotal={officialTotalCents / 100}
          personalTotal={personalTotal}
          combinedTotal={combinedTotal}
          currencySymbol={currencySymbol}
          onDownloadPdf={downloadPdf}
          downloadDisabled={officialLoading || filteredOfficial.length === 0}
          downloading={downloadingPdf}
        />

        {/* ============ OFFICIAL GIVING RECORDS SECTION ============ */}
        <OfficialGivingRecordsSection
          loading={officialLoading}
          records={filteredOfficial}
          fullYearRecords={officialForYear}
          currencySymbol={currencySymbol}
          hasAnyForYear={officialHasAny}
          viewYear={viewYear}
          dateFrom={officialDateFrom}
          dateTo={officialDateTo}
          categories={officialCategories}
          onDateFrom={setOfficialDateFrom}
          onDateTo={setOfficialDateTo}
          onToggleCategory={toggleOfficialCategory}
          onClearFilters={clearOfficialFilters}
          anyFilterActive={anyOfficialFilterActive}
        />

        {/* ============ MEMBER TAX RECEIPTS SECTION ============ */}
        <MemberReceiptsSection
          receipts={receipts}
          currencySymbol={currencySymbol}
          appSettings={appSettings}
        />

        {/* Divider between Official and Personal sections */}
        <div style={{ margin: "8px 0 18px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: "color-mix(in srgb, var(--kc-accent) 25%, transparent)" }} />
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--kc-accent)" }}>
            My Personal Giving Notes
          </div>
          <div style={{ flex: 1, height: 1, background: "color-mix(in srgb, var(--kc-accent) 25%, transparent)" }} />
        </div>

        {/* Period selector for personal ledger */}
        <div data-testid="giving-ytd-card" style={{ background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 14, padding: 18, marginBottom: 22, textAlign: "center" }}>
          <div style={{ fontSize: 10, opacity: 0.6, textTransform: "uppercase", letterSpacing: 1 }}>Personal ledger — quick period</div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
            {["Day", "Week", "Month", "Year"].map(p => {
              const active = period === p;
              return (
                <button
                  key={p}
                  data-testid={`giving-period-${p.toLowerCase()}`}
                  onClick={() => setPeriod(p)}
                  style={{
                    padding: "6px 14px", borderRadius: 999,
                    background: active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 8%, transparent)",
                    color: active ? "var(--kc-on-accent)" : "var(--kc-sub)",
                    border: `1px solid ${active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 30%, transparent)"}`,
                    fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
                    letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer",
                  }}
                >{p}</button>
              );
            })}
          </div>
          <div data-testid="giving-period-subtotal" style={{ marginTop: 12, fontSize: 13, color: "var(--kc-sub)" }}>
            <strong style={{ color: "var(--kc-text)" }}>{fmtCurrency(periodSubtotal, currencySymbol)}</strong> this {period.toLowerCase()}
          </div>
        </div>

        {error && (
          <div data-testid="giving-error" style={{
            background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.40)",
            color: "#ff6b6b", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13,
          }}>{error}</div>
        )}

        {/* Personal ledger year sections */}
        {loading ? (
          <div data-testid="giving-loading" style={{ background: "var(--kc-elevated)", borderRadius: 12, padding: 30, textAlign: "center", color: "var(--kc-sub)" }}>
            Loading your ledger…
          </div>
        ) : yearGroups.length === 0 ? (
          <div data-testid="giving-empty" style={{ background: "var(--kc-elevated)", border: "1px dashed color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 12, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 8, display: "inline-flex", justifyContent: "center" }}><CustomIcon name="dove" size={40} /></div>
            <div style={{ fontFamily: "'Cinzel',serif", color: "var(--kc-accent)", fontSize: 14, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
              No giving recorded yet
            </div>
            <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>
              Tap <strong style={{ color: "var(--kc-accent)" }}>+ New Entry</strong> to log your first gift.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {yearGroups.map(group => {
              const isCurrent = group.year === currentYear;
              const expanded = isCurrent ? true : !!expandedYears[group.year];
              return (
                <div key={group.year} data-testid={`giving-year-${group.year}`}
                     style={{ background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, overflow: "hidden" }}>
                  <button
                    data-testid={`giving-year-toggle-${group.year}`}
                    disabled={isCurrent}
                    onClick={() => setExpandedYears(p => ({ ...p, [group.year]: !p[group.year] }))}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 16px", background: isCurrent ? "color-mix(in srgb, var(--kc-accent) 8%, transparent)" : "transparent",
                      border: "none", color: "var(--kc-text)", cursor: isCurrent ? "default" : "pointer",
                      borderBottom: expanded ? "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "none",
                    }}
                  >
                    <span style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", color: "var(--kc-accent)" }}>
                      {group.year}{isCurrent ? " (current)" : ""}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span data-testid={`giving-year-total-${group.year}`} style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "var(--kc-text)" }}>
                        {fmtCurrency(group.total, currencySymbol)}
                      </span>
                      {!isCurrent && (
                        <span style={{ color: "var(--kc-sub)", fontSize: 12 }}>{expanded ? "▴" : "▾"}</span>
                      )}
                    </span>
                  </button>
                  {expanded && (
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                      {group.rows.map(it => (
                        <li key={it.id} data-testid={`giving-row-${it.id}`}
                            style={{
                              display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                              borderTop: "1px solid rgba(255,255,255,0.06)",
                              cursor: readonly ? "default" : "pointer",
                            }}
                            onClick={() => { if (!readonly) { setEditEntry(it); setShowForm(true); } }}>
                          <span style={{ fontSize: 18, width: 28, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            <CatIcon category={it.category} size={20} />
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kc-text)" }}>{it.category}</div>
                            <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>
                              {it.date}{it.notes ? ` · ${it.notes}` : ""}
                            </div>
                          </div>
                          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 15, fontWeight: 700, color: "var(--kc-accent)" }}>
                            {fmtCurrency(it.amount, currencySymbol)}
                          </div>
                          {!readonly && (
                            <button
                              className="btn btn-sm" data-testid={`giving-delete-${it.id}`} title="Delete entry"
                              onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                              style={{ background: "rgba(255,107,107,0.10)", color: "#ff6b6b", border: "1px solid rgba(255,107,107,0.40)", padding: "4px 8px" }}
                              aria-label="Delete entry"
                            ><CustomIcon name="trash" size={13} /></button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {readonly && (
          <div data-testid="giving-readonly-banner" style={{ marginTop: 18, padding: "10px 14px", borderRadius: 10, background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", fontSize: 11, color: "var(--kc-sub)", textAlign: "center" }}>
            Read-only Admin view — you cannot modify another user&apos;s giving records.
          </div>
        )}
      </div>

      {showForm && !readonly && (
        <GivingFormModal
          userId={targetUserId}
          entry={editEntry}
          onClose={() => { setShowForm(false); setEditEntry(null); }}
          onSaved={async () => { setShowForm(false); setEditEntry(null); await reload(); }}
        />
      )}
    </div>
  );
}

// =============================================================
// CombinedSummaryCard — top-of-page year-scoped totals.
// =============================================================
function CombinedSummaryCard({
  viewYear, allYears, onYearChange,
  officialTotal, personalTotal, combinedTotal,
  currencySymbol, onDownloadPdf, downloadDisabled, downloading,
}) {
  return (
    <div
      data-testid="giving-summary-card"
      style={{
        background: "linear-gradient(135deg, color-mix(in srgb, var(--kc-accent) 14%, transparent), color-mix(in srgb, var(--kc-accent) 4%, transparent))",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)",
        borderRadius: 16,
        padding: 22,
        marginBottom: 22,
      }}
    >
      {/* Top strip: year selector + PDF button */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase", flex: 1 }}>
          Giving Summary
        </div>
        <label style={{ fontSize: 11, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginRight: 4 }} htmlFor="giving-summary-year">Year</label>
        <select
          id="giving-summary-year"
          data-testid="giving-summary-year"
          className="form-input"
          value={viewYear}
          onChange={(e) => onYearChange(Number(e.target.value))}
          style={{ width: 110, height: 40, padding: "6px 10px", lineHeight: 1.2, fontSize: 14 }}
        >
          {allYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={onDownloadPdf}
          disabled={downloadDisabled || downloading}
          data-testid="giving-download-pdf"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            opacity: downloadDisabled ? 0.55 : 1,
          }}
          title={downloadDisabled ? "No official records to download for this period" : "Download a branded PDF of your official giving"}
        >
          <CustomIcon name="document" size={13} />
          {downloading ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      {/* Reconciliation banner (Item 5) — replaces the combined total tile */}
      {(() => {
        const diff = Math.round((officialTotal - personalTotal) * 100) / 100;
        const matches = Math.abs(diff) < 0.005; // treat sub-cent as match
        const fmt = (n) => `${currencySymbol}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return (
          <div
            data-testid={matches ? "giving-recon-match" : "giving-recon-diff"}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px", marginBottom: 14, borderRadius: 12,
              background: matches ? "rgba(74,222,128,0.10)" : "rgba(245,158,11,0.10)",
              border: `1px solid ${matches ? "rgba(74,222,128,0.40)" : "rgba(245,158,11,0.40)"}`,
              color: matches ? "#4ade80" : "#f59e0b",
              fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 14,
            }}
          >
            <CustomIcon name={matches ? "check" : "warning"} size={20} />
            <span data-testid="giving-recon-text">
              {matches
                ? `Records match — ${fmt(officialTotal)}`
                : `Difference of ${fmt(diff)}`}
            </span>
          </div>
        );
      })()}

      {/* Two totals side by side (Official + Personal) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
        <SummaryStat
          label="Official Giving"
          amount={officialTotal}
          currencySymbol={currencySymbol}
          testid="giving-summary-official"
          note="Finance-recorded"
        />
        <SummaryStat
          label="Personal Giving"
          amount={personalTotal}
          currencySymbol={currencySymbol}
          testid="giving-summary-personal"
          note="Your private ledger"
        />
      </div>
    </div>
  );
}

function SummaryStat({ label, amount, currencySymbol, testid, note, highlight = false }) {
  return (
    <div
      data-testid={testid}
      style={{
        padding: "14px 12px",
        borderRadius: 12,
        background: highlight ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "rgba(0,0,0,0.28)",
        border: `1px solid ${highlight ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 20%, transparent)"}`,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 10, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: highlight ? 28 : 24, fontWeight: 700, color: "var(--kc-accent)", marginTop: 6, lineHeight: 1.1 }}>
        {fmtCurrency(amount, currencySymbol)}
      </div>
      {note && <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4 }}>{note}</div>}
    </div>
  );
}

// =============================================================
// OfficialGivingRecordsSection — controlled by parent.
// Renders the filter row + record list. Hides if the member
// has literally zero official records for any period.
// =============================================================
function OfficialGivingRecordsSection({
  loading, records, fullYearRecords, currencySymbol, hasAnyForYear, viewYear,
  dateFrom, dateTo, categories,
  onDateFrom, onDateTo, onToggleCategory, onClearFilters, anyFilterActive,
}) {
  const filteredTotal = useMemo(
    () => records.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
    [records]
  );

  if (loading) return null;
  if (!hasAnyForYear && !anyFilterActive) return null;

  return (
    <div
      data-testid="giving-official-records-section"
      style={{
        background: "linear-gradient(135deg, color-mix(in srgb, var(--kc-accent) 10%, transparent), color-mix(in srgb, var(--kc-accent) 3%, transparent))",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)",
        borderRadius: 14,
        padding: 20,
        marginBottom: 22,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <CustomIcon name="coin" size={22} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 15, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase" }}>
            Official Giving Records
          </div>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>
            Recorded by the Finance team · {viewYear} · Read-only
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, opacity: 0.65, textTransform: "uppercase", letterSpacing: 1 }}>Filtered Total</div>
          <div data-testid="giving-official-filtered-total" style={{ fontSize: 17, fontWeight: 700, color: "var(--kc-accent)" }}>{fmtCents(filteredTotal, currencySymbol)}</div>
        </div>
      </div>

      {/* --------- Filter controls --------- */}
      <div
        data-testid="giving-official-filters"
        style={{
          background: "rgba(0,0,0,0.25)", borderRadius: 10, padding: "10px 12px",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", marginBottom: 14,
          display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Date from</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={(e) => onDateFrom(e.target.value)}
              data-testid="giving-official-date-from"
              style={{ height: 34 }}
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Date to</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={(e) => onDateTo(e.target.value)}
              data-testid="giving-official-date-to"
              style={{ height: 34 }}
            />
          </div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={onClearFilters}
            disabled={!anyFilterActive}
            data-testid="giving-official-clear-filters"
            style={{ opacity: anyFilterActive ? 1 : 0.5, height: 34 }}
          >
            Clear Filters
          </button>
        </div>
        <div>
          <div style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>Categories</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="giving-official-category-chips">
            {OFFICIAL_CATEGORIES.map(c => {
              const active = categories.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onToggleCategory(c)}
                  data-testid={`giving-official-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  style={{
                    padding: "5px 10px", borderRadius: 999,
                    border: `1px solid ${active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 30%, transparent)"}`,
                    background: active ? "color-mix(in srgb, var(--kc-accent) 22%, transparent)" : "transparent",
                    color: active ? "var(--kc-accent)" : "var(--kc-sub)",
                    fontSize: 11, fontWeight: 700, cursor: "pointer",
                  }}
                >{c}</button>
              );
            })}
          </div>
        </div>
      </div>

      {/* --------- Records --------- */}
      {records.length === 0 ? (
        <div data-testid="giving-official-nomatch" style={{ opacity: 0.6, padding: 16, textAlign: "center", fontSize: 12 }}>
          {anyFilterActive
            ? `No official records match your filters for ${viewYear}.`
            : `No official records in ${viewYear} yet.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {records
            .slice()
            .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
            .map(r => (
              <div key={r.recordId} data-testid={`giving-official-record-${r.recordId}`}
                   style={{
                     display: "flex", alignItems: "center", gap: 10,
                     padding: "10px 12px", background: "rgba(0,0,0,0.25)",
                     borderRadius: 8, border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)",
                   }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {fmtCents(r.amountCents, currencySymbol)}
                    <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, opacity: 0.7 }}>
                      · {r.category === "Other" ? (r.categoryOther || "Other") : r.category}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2, fontFamily: "monospace" }}>
                    {r.date} · {r.transactionRef}
                    {r.paymentMethod && <> · {r.paymentMethod}</>}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
// PDF generation — jsPDF (dynamic import to keep initial bundle small).
// Layout:
//   [logo] [Church name — Cinzel]
//   Member: {full name}
//   Period: {year} or {from} – {to}
//   -----
//   [Date | Category | Amount] table
//   -----
//   Total: {gold}
// =============================================================
async function generateGivingPdf({ appSettings, currencySymbol, member, viewYear, dateFrom, dateTo, records }) {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const GOLD = "var(--kc-accent)";
  const DARK = "#1A1A1A";

  const churchName = appSettings?.appName || "Kingdom Connect";
  const memberName =
    (`${member?.firstName || ""} ${member?.lastName || ""}`.trim())
    || (member?.name || "").trim()
    || "Member";
  const logoUrl = resolveLogo(appSettings?.appLogo, true);

  // Try to embed the logo. Silently continue if the fetch fails.
  let logoDataUrl = null;
  try {
    if (logoUrl) {
      const resp = await fetch(logoUrl, { cache: "no-store" });
      const blob = await resp.blob();
      logoDataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(blob);
      });
    }
  } catch { /* ignore, PDF will still render without logo */ }

  // ---- Header ----
  let y = margin;
  if (logoDataUrl) {
    try { pdf.addImage(logoDataUrl, "PNG", margin, y, 46, 46); } catch { /* format may not be PNG */ }
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(GOLD);
  pdf.text(churchName, margin + (logoDataUrl ? 60 : 0), y + 22);
  pdf.setFontSize(11);
  pdf.setTextColor(80);
  pdf.text("Giving Statement", margin + (logoDataUrl ? 60 : 0), y + 40);

  y += 66;
  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.7);
  pdf.line(margin, y, pageWidth - margin, y);

  // ---- Meta ----
  y += 20;
  pdf.setTextColor(DARK);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Recipient:", margin, y);
  pdf.setFont("helvetica", "normal");
  pdf.text(memberName, margin + 80, y);

  y += 16;
  const periodLabel = dateFrom || dateTo
    ? `${dateFrom || "…"}  →  ${dateTo || "…"}`
    : `Full year ${viewYear}`;
  pdf.setFont("helvetica", "bold");
  pdf.text("Period:", margin, y);
  pdf.setFont("helvetica", "normal");
  pdf.text(periodLabel, margin + 80, y);

  y += 16;
  pdf.setFont("helvetica", "bold");
  pdf.text("Generated:", margin, y);
  pdf.setFont("helvetica", "normal");
  pdf.text(new Date().toISOString().slice(0, 19).replace("T", " "), margin + 80, y);

  y += 22;
  pdf.setDrawColor(200);
  pdf.setLineWidth(0.3);
  pdf.line(margin, y, pageWidth - margin, y);

  // ---- Table ----
  y += 18;
  const colDate = margin;
  const colCat  = margin + 110;
  const colRef  = margin + 260;
  const colAmt  = pageWidth - margin;

  pdf.setFontSize(10);
  pdf.setTextColor(GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.text("Date",     colDate, y);
  pdf.text("Category", colCat,  y);
  pdf.text("Ref",      colRef,  y);
  pdf.text("Amount",   colAmt,  y, { align: "right" });
  y += 6;
  pdf.setDrawColor(GOLD);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 14;

  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(30);

  const sorted = [...records].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  let total = 0;
  for (const r of sorted) {
    if (y > pageHeight - margin - 60) {
      pdf.addPage();
      y = margin;
    }
    const cat = r.category === "Other" ? (r.categoryOther || "Other") : r.category;
    const amountStr = fmtCents(r.amountCents, currencySymbol);
    total += Number(r.amountCents) || 0;
    pdf.text(String(r.date || ""), colDate, y);
    pdf.text(String(cat || ""), colCat, y);
    pdf.setFontSize(8);
    pdf.text(String(r.transactionRef || ""), colRef, y);
    pdf.setFontSize(10);
    pdf.text(amountStr, colAmt, y, { align: "right" });
    y += 16;
  }

  // ---- Total ----
  y += 4;
  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.7);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 20;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(DARK);
  pdf.text(`${sorted.length} record${sorted.length === 1 ? "" : "s"}`, margin, y);
  pdf.setTextColor(GOLD);
  pdf.text(`Total: ${fmtCents(total, currencySymbol)}`, colAmt, y, { align: "right" });

  // ---- Footer ----
  const footer = `${churchName} — Generated from Kingdom Connect`;
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(160);
  pdf.text(footer, pageWidth / 2, pageHeight - 22, { align: "center" });

  const safeName = memberName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "") || "Member";
  const filename = `Kingdom-Connect-My-Giving-${safeName}-${viewYear}.pdf`;
  pdf.save(filename);
}

// =============================================================
// MemberReceiptsSection — appears on My Giving whenever the
// member has ≥1 official tax receipt on file. Hidden entirely
// when the receipts array is empty.
// =============================================================
function MemberReceiptsSection({ receipts, currencySymbol, appSettings }) {
  const [downloadingId, setDownloadingId] = useState("");
  if (!receipts || receipts.length === 0) return null;
  const sorted = [...receipts].sort((a, b) => (b.issuedAt || "").localeCompare(a.issuedAt || ""));

  const download = async (r) => {
    setDownloadingId(r.receiptId);
    try {
      await generateReceiptPdf({ receipt: r, appSettings, currencySymbol });
    } catch (e) {
      alert(e?.message || "Could not download receipt");
    } finally { setDownloadingId(""); }
  };

  return (
    <div
      data-testid="giving-receipts-section"
      style={{
        background: "linear-gradient(135deg, color-mix(in srgb, var(--kc-accent) 10%, transparent), color-mix(in srgb, var(--kc-accent) 3%, transparent))",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)",
        borderRadius: 14,
        padding: 20,
        marginBottom: 22,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <CustomIcon name="document" size={22} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 15, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase" }}>
            Tax Receipts
          </div>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>
            {sorted.length} receipt{sorted.length === 1 ? "" : "s"} on file · Tap to re-download the PDF
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sorted.map(r => (
          <div
            key={r.receiptId}
            data-testid={`giving-receipt-row-${r.receiptId}`}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
              background: "rgba(0,0,0,0.25)", borderRadius: 8, border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)",
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--kc-accent)" }}>
                {r.year} · {r.receiptNumber}
              </div>
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                Issued {r.issuedDate} · Total {currencySymbol}{(Number(r.totalCents) / 100).toFixed(2)}
              </div>
            </div>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => download(r)}
              disabled={downloadingId === r.receiptId}
              data-testid={`giving-receipt-download-${r.receiptId}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <CustomIcon name="document" size={12} />
              {downloadingId === r.receiptId ? "Preparing…" : "Download Receipt"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
